import Icon from "@/icons";
import { Channel, Message } from "types";
import TopChannel from "./Top/TopChannel";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";
import ErrorBox from "@/components/Error/Box";
import TextChannelMessage from "@/components/Channel/Text/Message";
import { useParams } from "react-router-dom";
import MsgInput from "./Input";
import getPermissionsById from "@/components/Dialogs/ChannelSettings/getPermissions";

interface EnhancedMessage extends Message {
    failedToLoadReplies?: boolean;
    state: string;
}
import emojis from "@/components/markdown/view/data/emojis"
import ErrorBoundary from "@/ErrorBoundary";
import toast from "react-hot-toast";

export default function TextChannel({ channel, isDM }: { channel: Channel; isDM?: boolean }) {
    const [canBeSend, setSend] = useState(false);
    const observerElem = useRef<HTMLDivElement>(null);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [messages, setMessages] = useState<EnhancedMessage[]>([]);
    const [lastMessageId, setLastMessageId] = useState<string | null>(null);
    const [, setMessagesContainer] = useState<HTMLDivElement | null>(null);
    const { channelId, teamId } = useParams<{ channelId: string; teamId: string }>();
    const [attachments, setAttachments] = useState<any[]>([]);
    const [replyTo, setReplyTo] = useState<Message | null>(null);
    const queryClient = useQueryClient();
    const user = queryClient.getQueryData<{ user: any }>(["user"])?.user;
    const userData = queryClient.getQueryData<any>(["user"]);
    const channels = queryClient.getQueryData<{ channels: any[] }>(["team", teamId, "channels"])?.channels;
    const team = queryClient.getQueryData<any>(["team", teamId])?.team;
    const roles = queryClient.getQueryData<any>(["team", teamId, "roles"])?.roles;
    const members = queryClient.getQueryState<any>(["team", teamId, "members"]);

    const messageQuery = useInfiniteQuery({
        queryKey: ["messages", channelId],
        queryFn: async ({ pageParam = 0 }) => {
            const response = await fetch(`__SITE_BASE__/api/v1/channels/${channelId}/messages?offset=${pageParam}&limit=15`);
            if (!response.ok) throw new Error("Network response was not ok");
            return response.json();
        },
        getNextPageParam: (lastPage) => lastPage.messages.length === 15 ? lastPage.messages.length : undefined,
        initialPageParam: 0,
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const allEmojis = useQuery({
        queryKey: ["emojis"],
        queryFn: async () => {
            const data = await fetch("__SITE_BASE__/api/me/reactions");
            const json = await data.json();
            return [...emojis, ...json.reactions];
        },
        retry: 1,
        retryDelay: 1000,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    const sendMessage = async () => {
        if (!textareaRef.current || !canBeSend) return;

        const message = textareaRef.current.value.trim();
        textareaRef.current.value = "";
        setSend(false);
        setReplyTo(null);
        setAttachments([]);
        const nonce = Math.random().toString(36).substring(2, 12) + Math.random().toString(36).substring(2, 12);
        const tempmsgid = `sending-${Math.random()}`;

        try {
            const nmessages = [...(messageQuery?.data?.pages.flatMap((page: any) => page.messages) || []), ...messages]
                .filter((msg, index, self) => index === self.findIndex((t) => t.id === msg.id && t.channelId === channelId))
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

            nmessages.push({
                id: tempmsgid,
                content: message,
                createdAt: new Date().toISOString(),
                createdBy: user,
                channelId,
                state: "sending",
                attachments,
                nonce,
            });

            queryClient.setQueryData(["messages", channelId], {
                pages: [{ messages: nmessages }],
                pageParams: [0],
                hasNextPage: messageQuery.hasNextPage,
            });

            const messageMap = new Map(nmessages.map((msg) => [msg.id, msg]));
            nmessages.forEach((msg) => {
                if (msg.replyTo) {
                    const replyMessage = messageMap.get(String(msg.replyTo));
                    if (replyMessage) msg.replyToMessage = replyMessage;
                    else msg.failedToLoadReplies = true;
                }
            });

            const body = {
                content: message,
                nonce,
                ...(replyTo && { replyTo: replyTo.id }),
                ...(attachments.length > 0 && { attachments }),
            };
            const response = await fetch(`__SITE_BASE__/api/v1/channels/${channelId}/messages`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            if (!response.ok) throw new Error((await response.json()).message);
        } catch (e: any) {
            toast.error(e.message);
            const nmessages = [...(messageQuery?.data?.pages.flatMap((page: any) => page.messages) || []), ...messages]
                .filter((msg, index, self) => index === self.findIndex((t) => t.id === msg.id && t.channelId === channelId))
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

            queryClient.setQueryData(["messages", channelId], {
                pages: [{ messages: nmessages.map((msg) => (msg.id === tempmsgid ? { ...msg, state: "failed" } : msg)) }],
                pageParams: [0],
                hasNextPage: messageQuery.hasNextPage,
            });

            setMessages((prevMessages) => prevMessages.map((msg) => (msg.id === tempmsgid ? { ...msg, state: "failed" } : msg)));
        } finally {
            if (textareaRef.current) textareaRef.current.style.height = "auto";
        }
    };

    const [streamerMode, setStreamerMode] = useState(() => {
        try {
            const storedMode = localStorage.getItem("streamerMode");
            if (!storedMode) {
                localStorage.setItem(
                    "streamerMode",
                    JSON.stringify({
                        enabled: false,
                        shortcut: null,
                        details: {
                            hidePersonalInfo: true,
                            hideInviteLinks: true,
                            hideLastMessagesOnDMs: true,
                        },
                    })
                );
            }
            return storedMode
                ? JSON.parse(storedMode)
                : {
                    enabled: false,
                    shortcut: null,
                    details: {
                        hidePersonalInfo: true,
                        hideInviteLinks: true,
                        hideLastMessagesOnDMs: true,
                    },
                };
        } catch {
            return {};
        }
    });

    useEffect(() => {
        const handleStorageChange = () => {
            const newValue = JSON.parse(localStorage.getItem("streamerMode") || "{}");
            setStreamerMode(newValue);
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, []);

    useEffect(() => {
        if (messageQuery.data) {
            const nmessages = [...(messageQuery.data.pages.flatMap((page: any) => page.messages) || []), ...messages]
                .filter((msg, index, self) => index === self.findIndex((t) => t.id === msg.id && t.channelId === channelId))
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            const lastMessage = nmessages[nmessages.length - 1];
            if (lastMessage) setLastMessageId(lastMessage.id);

            const messageMap = new Map(nmessages.map((msg) => [msg.id, msg]));
            nmessages.forEach((msg) => {
                if (msg.replyTo) {
                    const replyMessage = messageMap.get(String(msg.replyTo));
                    if (replyMessage) msg.replyToMessage = replyMessage;
                    else msg.failedToLoadReplies = true;
                }
            });
            setMessages(nmessages);
        }
    }, [messageQuery.data?.pages, channelId]);

    useEffect(() => {
        if (observerElem.current) {
            const observer = new IntersectionObserver(
                async (entries) => {
                    if (entries[0].isIntersecting && lastMessageId) await messageQuery.fetchNextPage();
                },
                { threshold: 0.5 }
            );
            observer.observe(observerElem.current);
            return () => observer.disconnect();
        }
    }, [lastMessageId, messageQuery]);

    const [channelPermission, setChannelPermission] = useState<any>({ allow: [], deny: [] });
    useEffect(() => {
        setChannelPermission(
            getPermissionsById({
                teamData: { data: { team } } as any,
                userId: user?.id,
                channelData: channels?.find((channel: any) => channel.id === channelId) as any,
                members: members?.data,
                roles,
            })
        );
    }, [channels, members?.data, roles, team, user?.id, channelId]);
    
    const allmsgs = [...(messageQuery?.data?.pages.flatMap((page: any) => page.messages) || []), ...messages]
        .filter((msg, index, self) => index === self.findIndex((t) => t.id === msg.id && t.channelId === channelId))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());


    const memoizedMessages = allmsgs
        ?.filter((msg, _index, self) => {
            if (msg.state === "sending") {
                const pendingNonce = msg.nonce;
                const hasNotPending = self.find((m) => m.nonce === pendingNonce && m.state !== "sending");
                return !hasNotPending;
            }
            return msg.state !== "deleted";
        })
        ?.map((message) => (
            <TextChannelMessage
                key={message.id + message.createdAt}
                message={message}
                user={{
                    ...user,
                    roles: members?.data?.members?.find((member: any) => member.id === message.createdBy.id)?.roles,
                }}
                permtodelete={channelPermission?.allow?.includes("MANAGE_MESSAGES") || message.createdBy.id === user?.id}
                replyTo={setReplyTo}
                isDM={Boolean(isDM)}
                streamerMode={streamerMode}
            />
        ));

    const unblockUser = async (userId: string) => {
        await fetch(`__SITE_BASE__/api/me/users/${userId}/block`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
        });
        queryClient.setQueryData(["user"], (old: any) => {
            const updatedBlocked = old.blocked.filter((block: any) => block.userId !== userId);
            return { ...old, blocked: updatedBlocked };
        });
    };
    return (
        <div className="flex flex-col w-full max-h-full border sm:rounded-2xl border-brand-dark bg-brand-dark/50" key={channel.id + channelId}>
            <TopChannel channel={channel} />
            <ErrorBoundary>
                <div
                    ref={chatContainerRef}
                    className="flex-grow flex py-2 shadow-2xl flex-col-reverse overflow-auto overflow-x-hidden"
                    style={{ scrollbarWidth: "thin", scrollbarColor: "rgb(40, 160, 42) rgb(27 27 29)" }}
                >

                    {messageQuery.isLoading ? (
                        <div className="flex items-center justify-center h-screen flex-col gap-2 p-2 rounded-lg hover:bg-brand-darkest cursor-pointer">
                            <div className="w-32 h-8 animate-pulse rounded-lg bg-gray-600 font-semibold text-lg" />
                            <div className="w-1/2 h-8 animate-pulse rounded-lg bg-gray-600 font-semibold text-lg" />
                            <div className="w-1/2 h-8 animate-pulse rounded-lg bg-gray-600 font-semibold text-lg" />
                        </div>
                    ) : messageQuery.isError ? (
                        <div className="flex justify-center items-center w-full h-32">
                            <ErrorBox message={messageQuery.error?.toString()} />
                        </div>
                    ) : (
                        <>
                            <div className="flex flex-col gap-2 py-2 w-full">
                                {!messageQuery.hasNextPage && (
                                    <div className="items-center flex relative px-4 flex-col space-y-2 justify-start">
                                        <div className="absolute inset-x-0 rounded-t-2xl top-0 h-full bg-gradient-to-b from-brand-dark opacity-75" />
                                        {channel.type === "text" && (
                                            <div className="items-center bg-brand-back p-2 rounded-full">
                                                <Icon name="hashtag" size="52" color="#a3a3ac" />
                                            </div>
                                        )}
                                        {isDM && (
                                            <div className="items-center bg-brand-back p-2 rounded-full">
                                                <Icon name="message" size="38" color="#a3a3ac" />
                                            </div>
                                        )}
                                        {isDM ? (
                                            <>
                                                <p className="font-semibold text-2xl">
                                                    This is the beginning of your direct message history with {channel.name}.
                                                </p>
                                                <p className="text-gray-400">
                                                    Messages between you and {channel.name} are private.
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="font-semibold text-2xl">
                                                    Welcome to #{channel.name}
                                                </p>
                                                <p className="text-gray-400">
                                                    This is the start of the #{channel.name} channel.
                                                </p>
                                            </>
                                        )}

                                    </div>
                                )}
                                <div ref={observerElem} />

                                <div className="flex flex-col gap-2" ref={setMessagesContainer}>
                                    {memoizedMessages}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <div>

                    {isDM && userData?.blocked?.find((block: any) => block.userId == (channel as any).users[0]?.id) ? (
                        <div className="flex items-center justify-start gap-2 py-2 px-2 border-b rounded-lg border-brand-back select-none cursor-not-allowed bg-brand-top">
                            <div className="flex justify-between items-center gap-2 w-full">
                                <div className="flex items-center justify-center text-red-400 p-2 gap-4 rounded-full">
                                    <div className="relative inline-block">
                                        <Icon name="shield" size="24" />
                                        <div className="absolute top-0 text-white rounded-full w-6 h-6 flex items-center justify-center">
                                            <Icon name="x" size="12" />
                                        </div>
                                    </div>
                                    <p className="text-gray-400">You have blocked this user. Unblock them to send messages.</p>

                                </div>
                                <button
                                    onClick={() => unblockUser((channel as any).users[0].id)}
                                    className="bg-brand-green text-white flex gap-2 items-center justify-center p-2 rounded-lg hover:bg-brand-green-dark"
                                >
                                    <Icon name="shield" />
                                    Unblock
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {((isDM && user?.verified) || (channelPermission.allow.includes("SEND_MESSAGES") && user?.verified)) && (
                                <MsgInput
                                    replyTo={replyTo}
                                    setReplyTo={setReplyTo}
                                    sendMessage={sendMessage}
                                    textareaRef={textareaRef}
                                    setAttachments={setAttachments}
                                    setSend={setSend}
                                    canBeSend={canBeSend}
                                    channel={channel}
                                    attachments={attachments}
                                    members={members}
                                    me={user}
                                    emojis={allEmojis?.data}
                                />
                            ) || (
                                    <div className="flex items-center rounded-lg justify-start gap-2 py-2 px-2 border-b border-brand-back select-none cursor-not-allowed bg-brand-top">
                                        <div className="flex items-center justify-center text-red-400 p-2 gap-4 rounded-full">
                                            <div className="relative inline-block">
                                                <Icon name="shield" size="24" />
                                                <div className="absolute top-0 text-white rounded-full w-6 h-6 flex items-center justify-center">
                                                    <Icon name="x" size="12" />
                                                </div>
                                            </div>
                                            {!user.verified && (
                                                <p className="text-gray-400">Please verify your email to send messages in this channel.</p>
                                            ) || (
                                                    <p className="text-gray-400">You do not have permission to send messages in this channel.</p>
                                                )}
                                        </div>
                                    </div>
                                )}
                        </>
                    )}
                </div>
            </ErrorBoundary>
        </div>
    );
}
